package com.evoapps.evoshift.ui.roster

import androidx.lifecycle.compose.collectAsStateWithLifecycle
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.EditNote
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.testTag
import com.evoapps.evoshift.di.diViewModel
import com.evoapps.evoshift.domain.model.RosterIds
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.model.ShiftNote
import com.evoapps.evoshift.ui.LightGold
import com.evoapps.evoshift.ui.util.asString
import com.evoapps.evoshift.viewmodels.ShiftNoteKey
import com.evoapps.evoshift.viewmodels.ShiftNoteViewModel
import com.evoapps.evoshift.widgets.DialogAction
import com.evoapps.evoshift.widgets.DialogSection
import com.evoapps.evoshift.widgets.EvoSecondaryButton
import com.evoapps.evoshift.widgets.EvoTextField
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.announcement_too_long
import evoshift.composeapp.generated.resources.shift_note_hint
import evoshift.composeapp.generated.resources.shift_note_label
import evoshift.composeapp.generated.resources.shift_note_save
import evoshift.composeapp.generated.resources.shift_note_title
import org.jetbrains.compose.resources.stringResource

/** Test tags of the note's field and button. */
object ShiftNoteTags {
    const val FIELD = "shift_note_field"
    const val SAVE = "shift_note_save"
}

/**
 * SH-118: the manager's note for the person working [shift], inside the shift dialog: only they
 * and the managers see it, and on a published shift they get a notification. Saved with its own
 * button (the dialog's Save is about the shift); an empty note removes it.
 */
@Composable
fun ShiftNoteSection(shift: Shift) {
    val key = ShiftNoteKey(RosterIds.workplaceIdOf(shift.rosterId), shift.id)
    val viewModel = diViewModel<ShiftNoteKey, ShiftNoteViewModel>(key)
    val state by viewModel.uiState.collectAsStateWithLifecycle()
    var text by rememberSaveable(shift.id) { mutableStateOf("") }
    var edited by rememberSaveable(shift.id) { mutableStateOf(false) }
    // The saved note fills the field until the manager types (also after a failed save).
    LaunchedEffect(state.note, state.isLoading) { if (!edited || state.isError) text = state.note }
    val tooLong = text.trim().length > ShiftNote.MAX_NOTE

    DialogSection(stringResource(Res.string.shift_note_title)) {
        EvoTextField(
            value = text,
            onValueChange = { text = it; edited = true },
            label = stringResource(Res.string.shift_note_label),
            singleLine = false,
            minLines = 2,
            enabled = !state.isLoading,
            leadingIcon = Icons.Default.EditNote,
            error = stringResource(Res.string.announcement_too_long, ShiftNote.MAX_NOTE).takeIf { tooLong },
            supportingText = stringResource(Res.string.shift_note_hint),
            modifier = Modifier.testTag(ShiftNoteTags.FIELD)
        )
        EvoSecondaryButton(
            DialogAction(
                stringResource(Res.string.shift_note_save),
                { viewModel.save(text) },
                enabled = !state.isLoading && !tooLong && text.trim() != state.note,
                loading = state.isSaving
            ),
            Modifier.fillMaxWidth().testTag(ShiftNoteTags.SAVE)
        )
        state.message?.let {
            Text(
                it.asString(),
                style = MaterialTheme.typography.bodySmall,
                color = if (state.isError) Color(0xFFFF8A80) else LightGold
            )
        }
    }
}
